import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { cvApi, emptyResume } from "@/lib/cv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";
import {
  Wand2, ArrowLeft, ArrowRight, Loader2, CheckCircle2, Languages, ShieldCheck,
} from "lucide-react";

// AI Wizard: one question per screen, builds a full ResumeData payload via /api/cv/wizard/generate.

export default function ResumeWizard() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({ language: language === "ar" ? "ar" : "en" });
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    cvApi.wizardSteps(language === "ar" ? "ar" : "en").then((r) => setSteps(r.steps || [])).catch(() => {});
    cvApi.professions().then(setProfessions).catch(() => {});
  }, [token, language, navigate]);

  if (!steps.length) {
    return (
      <DashboardLayout title={t("المساعد الذكي للسيرة", "Resume AI Wizard")}>
        <div className="flex items-center justify-center p-20"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
      </DashboardLayout>
    );
  }

  const total   = steps.length;
  const current = steps[idx];
  const value   = answers[current.field] ?? "";
  const progress = Math.round(((idx + 1) / total) * 100);

  const setValue = (v) => setAnswers((a) => ({ ...a, [current.field]: v }));

  const next = () => {
    if (current.required && !String(value).trim()) {
      toast.error(t("هذه الخطوة مطلوبة", "This step is required"));
      return;
    }
    if (idx + 1 < total) setIdx(idx + 1);
    else generate();
  };
  const back = () => { if (idx > 0) setIdx(idx - 1); };

  const generate = async () => {
    setBuilding(true);
    try {
      // Ask backend to expand wizard answers into a complete ResumeData payload.
      const { data } = await cvApi.wizardGenerate(answers, token);
      // Persist as a new resume so the user can keep editing.
      const seed = emptyResume();
      seed.title = answers.target_role || t("سيرتي الذاتية", "My Resume");
      seed.profession = answers.profession || "general";
      seed.data = data;
      const created = await cvApi.create(seed, token);
      toast.success(t("تم إنشاء سيرتك بنجاح", "Resume created!"));
      navigate(`/cv/edit/${created.resume_id}?from=wizard`);
    } catch (e) {
      toast.error(t("فشل إنشاء السيرة. حاول مجدداً.", "Failed to build resume — please retry."));
    } finally {
      setBuilding(false);
    }
  };

  const inputCls = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-white w-full text-base focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("ساعدني خطوة بخطوة", "AI Wizard")}>
      <BackBar to="/cv" label_ar="مركز السيرة الذاتية" label_en="Resume Center"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/cv", ar: "السيرة الذاتية", en: "Resume Center" }, { ar: "المساعد الذكي", en: "AI Wizard" }]} />
      <div className="max-w-2xl mx-auto" data-testid="resume-wizard">
        {/* Progress */}
        <div className="mb-5">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span className="inline-flex items-center gap-1"><Wand2 size={12} className="text-yellow-400" />{t("المساعد الذكي", "AI Wizard")}</span>
            <span>{t("الخطوة", "Step")} {idx + 1} / {total}</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-yellow-500 to-amber-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Card */}
        <div className="glass-card rounded-3xl p-6 lg:p-8 space-y-5" data-testid={`wizard-step-${current.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center flex-shrink-0">
              <Wand2 className="text-black" size={20} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl lg:text-2xl font-bold text-white leading-snug">{current.title}</h2>
              {current.help && <p className="text-sm text-slate-400 mt-1">{current.help}</p>}
            </div>
          </div>

          <div>
            {current.type === "choice" && current.choices && (
              <div className="grid sm:grid-cols-2 gap-2" data-testid="wizard-choices">
                {current.choices.map((c) => (
                  <button key={c.value} onClick={() => setValue(c.value)}
                    className={`text-right border rounded-xl px-4 py-3 transition ${
                      value === c.value
                        ? "border-yellow-500 bg-yellow-500/10 text-yellow-100"
                        : "border-slate-700 text-slate-300 hover:border-slate-500"
                    }`}
                    data-testid={`wizard-choice-${c.value}`}>
                    {value === c.value && <CheckCircle2 size={14} className="inline ml-2 text-yellow-400" />}
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {current.type === "profession" && (
              <select value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} data-testid="wizard-profession">
                <option value="">{t("اختر تصنيف المهنة…", "Select profession category…")}</option>
                {professions.map((p) => (
                  <option key={p.slug} value={p.slug}>{language === "ar" ? p.ar : p.en}</option>
                ))}
              </select>
            )}

            {["text", "email", "tel"].includes(current.type) && (
              <Input
                type={current.type === "text" ? "text" : current.type}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={current.placeholder}
                className={inputCls}
                data-testid={`wizard-input-${current.id}`}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") next(); }}
              />
            )}
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-3 pt-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1"><Languages size={11} className="text-yellow-400" />{t("يدعم العربية والإنجليزية", "Arabic & English supported")}</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-400" />{t("بدون صورة أو معلومات شخصية", "No photo or personal info")}</span>
          </div>

          {/* Nav */}
          <div className="flex gap-2 pt-3">
            <Button onClick={back} disabled={idx === 0} variant="outline" className="border-slate-700 text-slate-300" data-testid="wizard-back-btn">
              <ArrowLeft size={14} className="ml-1" />{t("السابق", "Back")}
            </Button>
            <Button onClick={next} disabled={building} className="btn-primary flex-1" data-testid="wizard-next-btn">
              {building
                ? <><Loader2 size={14} className="animate-spin ml-2" />{t("جارٍ بناء سيرتك بالذكاء الاصطناعي…", "Building your resume with AI…")}</>
                : idx + 1 === total
                  ? <><Wand2 size={14} className="ml-2" />{t("ابنِ سيرتي الآن", "Build my resume now")}</>
                  : <>{t("التالي", "Next")}<ArrowRight size={14} className="mr-1" /></>
              }
            </Button>
          </div>
        </div>

        {/* Skip */}
        <div className="text-center mt-4">
          <button onClick={() => navigate("/cv")} className="text-xs text-slate-500 hover:text-slate-300 underline" data-testid="wizard-skip">
            {t("إلغاء والعودة لمركز السيرة", "Cancel & go back to Resume Center")}
          </button>
        </div>

        {/* Footnote */}
        <div className="mt-6 glass-card rounded-2xl p-4 text-xs text-slate-400 leading-relaxed">
          <Badge className="bg-yellow-500/15 text-yellow-300 mb-2 text-[10px]">{t("ما الذي سيحدث؟", "What happens next?")}</Badge>
          <p>
            {t(
              "سنحوّل إجاباتك القصيرة إلى سيرة احترافية متكاملة بمعايير السوق الأمريكي (Summary + خبرات + مهارات + إنجازات). يمكنك تعديل أي شيء بعد ذلك، ومراجعة سيرتك قبل التحميل.",
              "We'll convert your short answers into a complete US-standard resume (Summary + Experience + Skills + Achievements). You can edit anything afterwards and review it before download."
            )}
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
